import React, { useMemo } from "react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Printer, X } from "lucide-react";
import { PUBLIC_TRACK } from "@/constants/testIds";

function fmt(ts) {
  if (!ts) return "—";
  try {
    return new Date(ts).toLocaleString();
  } catch {
    return ts;
  }
}

export const ReceiptModal = ({ open, onOpenChange, shipment }) => {
  const barcodeBars = useMemo(() => {
    if (!shipment) return [];
    return shipment.tracking_number.split("").flatMap((c, i) =>
      [1, 2, 1, 3].map((w, j) => ({
        key: `${i}-${j}`,
        width: w,
        height: (c.charCodeAt(0) % 3) * 12 + 24,
      }))
    );
  }, [shipment]);

  if (!shipment) return null;
  const s = shipment;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl border-slate-700 bg-white p-0 text-slate-900 print:border-0 print:shadow-none">
        <div id="awb-receipt" className="p-8 font-mono text-sm">
          <div className="flex items-start justify-between border-b-2 border-slate-900 pb-4">
            <div>
              <h2 className="text-2xl font-black tracking-tight">GO DELIVER</h2>
              <p className="text-xs uppercase tracking-[0.2em] text-slate-500">Air Waybill / Freight Manifest</p>
            </div>
            <div className="text-right">
              <p className="text-xs text-slate-500">TRACKING NO.</p>
              <p className="text-lg font-bold">{s.tracking_number}</p>
            </div>
          </div>

          {/* Barcode representation */}
          <div className="my-4 flex h-14 items-end gap-[2px] overflow-hidden">
            {barcodeBars.map((bar) => (
              <span
                key={bar.key}
                className="bg-slate-900"
                style={{ width: `${bar.width}px`, height: `${bar.height}px` }}
              />
            ))}
          </div>

          <div className="grid grid-cols-2 gap-6">
            <div className="rounded border border-slate-300 p-3">
              <p className="mb-1 text-xs font-bold uppercase text-slate-500">Sender</p>
              <p className="font-bold">{s.sender_name}</p>
              <p className="text-slate-600">{s.sender_address || s.origin}</p>
              <p className="mt-2 text-xs text-slate-500">ORIGIN: {s.origin}</p>
            </div>
            <div className="rounded border border-slate-300 p-3">
              <p className="mb-1 text-xs font-bold uppercase text-slate-500">Recipient</p>
              <p className="font-bold">{s.receiver_name}</p>
              <p className="text-slate-600">{s.receiver_address || s.destination}</p>
              <p className="mt-2 text-xs text-slate-500">DESTINATION: {s.destination}</p>
            </div>
          </div>

          <div className="mt-4 grid grid-cols-4 gap-3 rounded border border-slate-300 p-3 text-xs">
            <div><p className="text-slate-500">WEIGHT</p><p className="font-bold">{s.weight} kg</p></div>
            <div><p className="text-slate-500">PACKAGE</p><p className="font-bold">{s.package_type}</p></div>
            <div><p className="text-slate-500">SERVICE</p><p className="font-bold">{s.service_tier}</p></div>
            <div><p className="text-slate-500">STATUS</p><p className="font-bold">{s.status}</p></div>
          </div>

          <div className="mt-4">
            <p className="mb-2 text-xs font-bold uppercase text-slate-500">Audit Trail</p>
            <table className="w-full text-left text-xs">
              <thead>
                <tr className="border-b border-slate-300 text-slate-500">
                  <th className="py-1">Status</th><th>Timestamp</th><th>Note</th>
                </tr>
              </thead>
              <tbody>
                {(s.status_history || s.timeline || []).map((h, i) => (
                  <tr key={`${h.status}-${h.timestamp}-${i}`} className="border-b border-slate-100">
                    <td className="py-1 font-bold">{h.status}</td>
                    <td>{fmt(h.timestamp)}</td>
                    <td className="text-slate-600">{h.note}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="mt-6 flex items-end justify-between border-t-2 border-dashed border-slate-400 pt-4">
            <p className="text-xs text-slate-500">Issued {fmt(s.created_at)} • This is a computer generated manifest.</p>
            <div className="text-center">
              <div className="h-10 w-40 border-b border-slate-900" />
              <p className="mt-1 text-xs text-slate-500">Authorized Signature</p>
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-2 border-t border-slate-200 p-4 print:hidden">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            <X className="mr-1 h-4 w-4" /> Close
          </Button>
          <Button
            data-testid={PUBLIC_TRACK.printButton}
            onClick={() => window.print()}
            className="bg-orange-600 text-white hover:bg-orange-700"
          >
            <Printer className="mr-1 h-4 w-4" /> Print / Save PDF
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
